import {Alert, Button, Input, Radio, Steps} from 'antd'
import {report_getDefaultReasons_Api} from 'apis'
import {getId} from 'apis/model/base'
import FieldsFactory from 'components/form/FieldsFactory'
import {SubmitButton} from 'components/form/FormActionBar'
import Pure from 'components/Pure'
import {Formik, FormikConsumer} from 'formik'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo, useState} from 'react'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import LoadingPage from 'views/LoadingPage'
import {getResponseItems, NullAsDefault} from 'views/Shared'

const ReasonSelect = ({ name, type, value, onChange }) => {
  const t = useTranslate()
  const apiInfo = report_getDefaultReasons_Api
  const cacheId = apiInfo.path.replace(':type', type)
  const {
    error,
    response,
    errorMessages,
    isLoading = false,
  } = useAsyncWithCache({
    cacheId,
    apiInfo,
    query: bindQueryParam({ type }),
  })
  if (isLoading) {
    return <LoadingPage />
  } else if (error) {
    return (
      <Alert
        type="warning"
        showIcon={true}
        message={errorMessages?.error}
        description={errorMessages?.error_code}
      />
    )
  }
  const options = getResponseItems(response)
  if (_.isEmpty(options)) {
    return (
      <Alert
        type="warning"
        showIcon={true}
        description={t('no data is available now')}
      />
    )
  }
  return (
    <Radio.Group
      name={name}
      value={value}
      className="flex flex-col gap-3 ml-2"
      onChange={onChange}>
      {Array.from(options ?? []).map((item, index) => (
        <Radio
          key={index}
          value={getId(item)}>
          <span className="flex flex-col">
            <span className="font-medium text-sm text-color-100 italic">
              {getTitle(item)}
            </span>
            <span className="text-xs text-color-500 italic leading-tight">
              {_.get(item, 'description')}
            </span>
          </span>
        </Radio>
      ))}
    </Radio.Group>
  )
}

const formSchema = {
  reason: {
    title: 'choose reason report',
    children: [
      {
        name: 'report_default_reason_id',
        component: createControlledFormField({
          Component: ReasonSelect,
          withValues: (values) => ({
            type: values?.owner_type,
          }),
        }),
      },
    ],
  },
  title: {
    title: 'title',
    children: [
      {
        name: 'title',
        component: createControlledFormField({
          placeholder: 'title',
        }),
      },
    ],
  },
  description: {
    title: 'description',
    children: [
      {
        name: 'description',
        component: createControlledFormField({
          rows: 3,
          Component: Input.TextArea,
          placeholder: 'description',
        }),
      },
    ],
  },
}

const ReportModal = ({
  owner_id,
  owner_type,
  header = null,
  footer = null,
  onSubmit = NullAsDefault,
  ...props
}) => {
  const t = useTranslate()

  const [current, setCurrent] = useState(0)

  const steps = useMemo(
    () => [
      {
        index: 0,
        title: undefined,
        description: undefined,
        formSchema: [formSchema.reason],
        footer: (
          <FormikConsumer>
            {({ values }) => {
              const canNext = values?.report_default_reason_id
              return (
                <div className="flex flex-center gap-2">
                  <Button
                    type="primary"
                    className="button-rounded-lg no-border"
                    disabled={!canNext}
                    onClick={() => {
                      if (canNext) {
                        setCurrent(current + 1)
                      }
                    }}>
                    {t('next')}
                  </Button>
                </div>
              )
            }}
          </FormikConsumer>
        ),
      },
      {
        index: 1,
        title: undefined,
        description: undefined,
        formSchema: [formSchema.title, formSchema.description],
        footer: (
          <div className="flex flex-center gap-2">
            <Button
              className="button-rounded-lg no-border"
              onClick={() => {
                setCurrent(current - 1)
              }}>
              {t('back')}
            </Button>
            <SubmitButton shape="round" />
          </div>
        ),
      },
    ],
    [current]
  )

  const initialValues = {
    owner_id,
    owner_type,
    title: undefined,
    description: undefined,
    report_default_reason_id: undefined,
  }

  const validate = (values) => {
    let errors = {}

    _.forEach(
      [
        'title',
        'owner_id',
        'owner_type',
        'description',
        'report_default_reason_id',
      ],
      (name) => {
        const value = _.get(values, name)
        if (_.isEmpty(value ?? undefined)) {
          errors[name] = t('required field')
        }
      }
    )
    return errors
  }

  const step = useMemo(
    () => _.find(steps, { index: Number(current) }),
    [current]
  )

  return (
    <Formik
      validate={validate}
      onSubmit={(values) => {
        onSubmit(values)
      }}
      initialValues={initialValues}>
      <div className="flex flex-col gap-4">
        <Steps
          size="small"
          className="w-1/2 mx-auto py-2 mb-2"
          current={current}
          onChange={(value) => {
            setCurrent(value)
          }}
          items={steps.map(({ title, description }) => ({
            title,
            description,
          }))}
        />
        {header}
        <React.Fragment>
          <Pure input={[step]}>
            <div className="flex flex-col gap-3">
              <FieldsFactory formSchema={step?.formSchema} />
            </div>
          </Pure>
          {step?.footer ?? null}
        </React.Fragment>
        {footer}
        <div className="h-3" />
      </div>
    </Formik>
  )
}

export default ReportModal
